<template>
  <v-layout
    class="full-height full-width pa-1"
    column
  >
    <v-alert
      class="full-width"
      type="warning"
      v-model="showScreenCaptureAlert"
    >
      <span class="white--text">
       Continue to record for 3-4 seconds at the end of the recording to ensure sound and content is not cut off. <a href="https://knowledge.playposit.com/article/231-uploading-videos-to-playposit-3-0#inst-howto" target="_blank">Click here</a> for more information.
      </span>
    </v-alert>
    <span
      v-if="showScreenCaptureAlert"
      role="alert"
      class="faux-hidden"
    >
      Continue to record for 3-4 seconds at the end of the recording to ensure sound and content is not cut off.
    </span>
    <v-alert
      class="full-width minimum-alert-height"
      type="warning"
      v-model="showOutdatedSafariAlert"
    >
      <span class="white--text">
        Please update to the latest version of Safari or use <a href="https://knowledge.playposit.com/article/231-uploading-videos-to-playposit-3-0" target="_blank">a different browser</a> in order to access webcam and screen recording.
      </span>
    </v-alert>
    <span
      v-if="showOutdatedSafariAlert"
      role="alert"
      class="faux-hidden"
    >
      Please update to the latest version of Safari or use a different browser in order to access webcam and screen recording.
    </span>
    <v-alert
      class="full-width minimum-alert-height"
      type="success"
      color="#4caf50"
      v-model="showUploadCompletionAlert"
    >
      <span class="white--text">
        Your video is fully uploaded! It is now transcoding. If you leave this page, you will be able to find the transcoding video in My Videos.
      </span>
    </v-alert>
    <span
      v-if="showUploadCompletionAlert"
      role="alert"
      class="faux-hidden"
    >
      Your video is fully uploaded! It is now transcoding. If you leave this page, you will be able to find the transcoding video in My Videos.
    </span>
    <div
      id="video-upload-uppy"
      class="full-height full-width uppy-dashboard-container"
      :class="{
        'hide-cancel-button': hideCancelButton,
      }"
    />
  </v-layout>
</template>

<script>
/* global _, dataLayer */
import {
  mapState,
  mapGetters,
} from 'vuex';
import '@uppy/core/dist/style.min.css';
import '@uppy/dashboard/dist/style.min.css';
import '@uppy/webcam/dist/style.min.css';
import '@uppy/screen-capture/dist/style.min.css';
import Uppy from '@uppy/core';
import Tus from '@uppy/tus';
import Dashboard from '@uppy/dashboard';
import ScreenCapture from '@uppy/screen-capture';
import Webcam from '@uppy/webcam';
import EventBus from '@snippets/js/eventBus'; // eslint-disable-line
import Cloudflare from '../js/uppy-cloudflare-plugin';
import Vimeo from '../js/uppy-vimeo-plugin';
import DigitalOcean from '../js/uppy-digitalocean-plugin';
import VideoTranscoderService from '../services/VideoTranscoderService';

export default {
  name: 'VideoUploadUppy',
  props: {
    token: {
      type: String,
      required: true,
    },
    teacherId: {
      type: Number,
      required: true,
    },
    startingKey: {
      type: String,
      validator: (sk) => ['webcam', 'screen', 'upload'].includes(sk),
    },
    videoHost: {
      type: String,
      validator: (videoHost) => ['vimeo', 'cloudflare', 'digitalocean'].includes(videoHost),
    },
    studentId: {
      type: Number,
      default: null,
    },
    width: {
      type: String,
      default: '100%',
    },
    height: {
      type: String,
      default: '100%',
    },
    maxSize: {
      type: String,
      default: '5000MB',
    },
    maxDuration: {
      type: Number,
      default: 3600,
    },
    showCloseButton: {
      type: [String, Boolean],
      default: true,
      validator: (scb) => [true, false, 'left'].includes(scb),
    },
    debug: {
      type: Boolean,
      default: false,
    },
    hostingLog: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    uppy: null,
    processing: false,
    source: null,
    hideCancelButton: false,
    isInScreenCastMode: false,
    showUploadCompletionAlert: false,
  }),
  computed: {
    ...mapState('videoTranscoder', [
      'cardTab',
      'queryString',
      'loading',
      'keyboardFocus',
      'userAgent',
    ]),
    ...mapGetters('videoTranscoder', [
      'mobile',
    ]),
    showScreenCaptureAlert() {
      return (
        this.isInScreenCastMode
        && _.get(this, 'userAgent.browser.name', '').toLowerCase() !== 'firefox'
      );
    },
    showOutdatedSafariAlert() {
      if (_.get(this, 'userAgent.browser.name', '').toLowerCase() !== 'safari') return false;
      const [
        majorVersion,
        minorVersion,
        patchVersion,
      ] = _.get(this, 'userAgent.browser.version')
        .split('.')
        .map(Number);
      return (
        majorVersion < 14
        || (
          minorVersion === 0
          && (
            !_.isFinite(patchVersion)
            || patchVersion < 2
          )
        )
      );
    },
    allowedFileTypes() {
      return _.get(this, 'hostingLog.hasLimitedVideoUploadAccess')
        ? ['video/mp4']
        : [
          'video/*',
          'application/x-troff-msvideo',
          'video/quicktime',
          'video/x-flv',
          '.mp4',
          '.mov',
          '.avi',
          '.webm',
          '.wmv',
          '.mkv',
          '*.mp4',
          '*.mov',
          '*.avi',
          '*.webm',
          '*.wmv',
          '*.mkv',
          '*.flv',
        ];
    },
  },
  methods: {
    initEvents() {
      EventBus.$on('verify-exit-permissions', (cb) => {
        if (this.cardTab !== 1) return;
        if (_.isFunction(cb)) cb(!this.processing);
      });
      EventBus.$on('cancel-transcode', () => {
        if (this.uppy) this.uppy.cancelAll();
      });
    },
    initObservers() {
      const targetElement = document.getElementById('video-upload-uppy');
      if (!targetElement) return;
      const observer = new MutationObserver(async () => {
        await this.$nextTick();
        this.isInScreenCastMode = !!document.getElementById('uppy-DashboardContent-panel--ScreenCapture');
      });
      observer.observe(targetElement, {
        childList: true,
        subtree: true,
      });
    },
    handleUppyComplete(result) {
      this.processing = false;
      if (_.get(result, 'successful', []).length) {
        this.$emit('success', result.successful[0]);
      }
    },
    handleUppyError(file, error) {
      this.processing = false;
      this.$emit('error', error);
    },
    handleCancelationAnalytics(file) {
      try {
        const source = _.get(file, 'source', '').toLowerCase();
        let uploadType = 'video file';
        if (source === 'screencapture') uploadType = 'screen recording';
        else if (source === 'webcam') uploadType = 'webcam recording';
        let userTypeMessage = `teacher ${this.teacherId}`;
        if (this.studentId) userTypeMessage = `student ${this.studentId}`;
        return dataLayer.manualLog(`A Cloudflare upload process initialized by ${userTypeMessage} has been canceled for the following ${uploadType}: ${file.name}`);
      } catch(e) {
        return this.$emit('error', e);
      }
    },
    async handleUppyCancelation(file) {
      try {
        this.processing = false;
        this.handleCancelationAnalytics(file);
        if (
          !file.playpositVideo
          || file.isFinished
          || this.videoHost === 'digitalocean'
        ) return false;
        const { data: hostingLog } = await VideoTranscoderService.updateTranscode({
          videoId: file.playpositVideo.id,
          ...this.videoHost === 'vimeo' && { vimeoStatus: 'canceled' },
          ...this.videoHost !== 'vimeo' && { cloudflareStatus: 'canceled' },
        });
        return hostingLog;
      } catch(e) {
        return this.$emit('error', e);
      }
    },
    async handleUppyUpload() {
      const [progressBar] = await this.$waitForElementToDisplay('.uppy-StatusBar-content', 250);
      progressBar.setAttribute('aria-live', 'polite');
      progressBar.setAttribute('aria-role', 'alert');
      progressBar.setAttribute('role', 'alert');
    },
    handleUppyTeacherVideoCreated() {
      if (!this.studentId) this.showUploadCompletionAlert = true;
    },
    initUppy() {
      this.uppy = null;
      // Default to using Cloudflare as the video host.
      const config = {
        playpositPlugin: Cloudflare,
        playpositPluginConfig: {
          teacherId: this.teacherId,
          studentId: this.studentId,
          headers: {
            Authorization: 'Bearer SkpfovtkYiwqj40wIuzEHmmq-0IJXbt2wUTuN-5q',
          },
        },
        tus: {
          endpoint: 'https://api.cloudflare.com/client/v4/accounts/ad6798efefe74f7b6198af66a777584c/media',
          headers: {
            Authorization: 'Bearer SkpfovtkYiwqj40wIuzEHmmq-0IJXbt2wUTuN-5q',
          },
          resume: true,
          chunkSize: 25 * 1024 * 1024, // 25 MB
        },
      };
      if (this.videoHost === 'vimeo') {
        config.playpositPlugin = Vimeo;
        config.playpositPluginConfig = {
          teacherId: this.teacherId,
          studentId: this.studentId,
        };
        config.tus = { resume: true };
        if (_.get(this, 'hostingLog.hasVimeoProxy')) config.tus.chunkSize = 25 * 1024 * 1024; // 25 MB
        else config.tus.chunkSize = 50 * 1024 * 1024; // 50 MB
      } else if (this.videoHost === 'digitalocean') {
        config.playpositPlugin = DigitalOcean;
        config.playpositPluginConfig = {
          teacherId: this.teacherId,
          studentId: this.studentId,
        };
        delete config.tus;
      }
      this.uppy = new Uppy({
        autoProceed: true,
        restrictions: {
          maxFileSize: _.get(this, 'hostingLog.hasLimitedVideoUploadAccess')
            ? 314572800 // 300MB in bytes
            : 5242880000, // 5GB in bytes
          maxNumberOfFiles: 1,
          allowedFileTypes: this.allowedFileTypes,
        },
        onBeforeFileAdded: (currentFile) => {
          this.hideCancelButton = false;
          this.showUploadCompletionAlert = false;
          const correctedName = currentFile.name.ppRemoveUnsafeUnicodeCharacters();
          return {
            ...currentFile,
            name: correctedName.length ? correctedName : `My Video ${new Date().toLocaleString()}`,
          };
        },
        onBeforeUpload: () => {
          this.processing = true;
        },
        locale: {
          uploading: '',
        },
      })
        .use(config.playpositPlugin, config.playpositPluginConfig)
        .use(Dashboard, {
          inline: true,
          target: '.uppy-dashboard-container',
          showLinkToFileUploadResult: false,
          showProgressDetails: true,
          note: 'Only video files are accepted, max 5GB.',
          proudlyDisplayPoweredByUppy: false,
          dropPaste: 'Drop files here, paste, or %{browse}',
          dropPasteImport: 'Drop files here, paste, %{browse}, or import from',
          height: '100%',
          width: '100%',
        });
      if (config.tus) this.uppy.use(Tus, config.tus);
      if (
        !_.get(this, 'hostingLog.hasLimitedVideoUploadAccess')
        && !this.showOutdatedSafariAlert
      ) {
        this.uppy
          .use(ScreenCapture, {
            target: Dashboard,
            preferredVideoMimeType: 'video/webm;codecs=VP9',
            showRecordingLength: true,
            videoConstraints: {
              height: {
                max: 1080,
                ideal: 1080,
              },
              width: {
                max: 1920,
                ideal: 1920,
              },
            },
            modes: [
              'video-audio',
              'video-only',
            ],
          })
          .use(Webcam, {
            target: Dashboard,
            preferredVideoMimeType: 'video/webm;codecs=VP9',
            showRecordingLength: true,
            videoConstraints: {
              height: {
                max: 1080,
                ideal: 1080,
              },
              width: {
                max: 1920,
                ideal: 1920,
              },
            },
            modes: [
              'video-audio',
              'video-only',
            ],
          });
      }
      this.uppy.on('upload', this.handleUppyUpload);
      this.uppy.on('complete', this.handleUppyComplete);
      this.uppy.on('upload-error', this.handleUppyError);
      this.uppy.on('teacher-video-created', this.handleUppyTeacherVideoCreated);
      this.uppy.on('error', this.handleUppyError);
      this.uppy.on('file-removed', this.handleUppyCancelation);
      this.uppy.on('upload-success', () => {
        this.hideCancelButton = true;
      });
    },
  },
  watch: {
    cardTab(n) {
      if (n === 1) {
        const inputElement = this.$el.querySelector('input.filepond--browser');
        if (inputElement && this.keyboardFocus) inputElement.focus();
      }
    },
  },
  beforeDestroy() {
    if (this.source) this.source.cancel();
    this.uppy.close();
  },
  async mounted() {
    await this.$nextTick();
    this.initUppy();
    this.initEvents();
    this.initObservers();
  },
};
</script>

<style lang="scss">
  #video-upload-uppy {
    &.hide-cancel-button {
      button.uppy-u-reset[title="Cancel"],
      button.uppy-u-reset[title="Remove file"],
      button.uppy-DashboardContent-back {
        display: none;
      }
    }

    video {
      max-width: 100%;
    }
  }

  .uppy-Root,
  .uppy-Dashboard {
    height: 100%;
  }

  .uppy-DashboardContent-bar {
    z-index: 2;
  }

  .uppy-Webcam-container,
  .uppy-Webcam-video {
    width: 100%;
  }

  .uppy-Webcam-buttonContainer {
    text-align: center;
  }

  .minimum-alert-height {
    min-height: 78px;
  }

  .faux-hidden {
    position: fixed;
    top: 10000px;
    left: 10000px;
    user-select: none;
  }
</style>